import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppState } from '../../app/state';
import { callAction } from '../../app/actions';
import { apiRequest } from '../../api/client';
import { helpText } from '../../i18n/help';
import { PresenceStack } from '../presence/PresenceStack';
import { LanguagePicker } from '../../components/LanguagePicker';
import { registerHandle } from '../../app/handles';

/**
 * The AI documentation context screen: the guidelines, glossary and grounding toggles applied
 * to every generation, on top of each node's own prompt.
 *
 * THE FIRST REAL FORM IN THE PORT, and that is why this small screen went next. The vanilla
 * version was uncontrolled in both directions: `openAiDocConfig()` fetched the config and
 * wrote each value into an element, and `saveAiDocConfig()` read them back out of the DOM.
 * Between those two the values existed nowhere else — so anything that re-rendered, replaced
 * or hid an input silently discarded what had been typed into it, and nothing would have
 * failed. The plan calls this out as the most likely source of silent data-loss regressions
 * in the whole migration.
 *
 * Here the form is state. It is loaded on open, edited in place, and sent on save; `dirty`
 * exists so closing without saving can be noticed rather than assumed away.
 *
 * The cache stays on the vanilla side. `aiDocCfgCache` is what lets the node popup label its
 * per-source "Inherit" options with what inheriting actually resolves to, and that popup has
 * not been ported — so this screen pushes the config back through an action after every load
 * and save, exactly as the vanilla code did inline.
 */

export interface AiDocConfigHandle {
  open: () => void;
  close: () => void;
}

interface DocConfig {
  designGuidelines: string;
  generalTerms: string;
  useExistingDoc: boolean;
  useNeighborDocs: boolean;
  useSemanticGrounding: boolean;
  useLinkedDocs: boolean;
}

/** What the server means by "not set". Every grounding source is on unless it says otherwise. */
const EMPTY: DocConfig = {
  designGuidelines: '',
  generalTerms: '',
  useExistingDoc: true,
  useNeighborDocs: true,
  useSemanticGrounding: true,
  useLinkedDocs: true,
};

const GROUNDING: { key: keyof DocConfig; id: string; label: string }[] = [
  {
    key: 'useExistingDoc',
    id: 'cfg-existing',
    label: "Use each node's existing documentation as context (revise instead of restart)",
  },
  {
    key: 'useNeighborDocs',
    id: 'cfg-neighbors',
    label: 'Use the documentation of neighboring nodes (parent, siblings, children)',
  },
  {
    key: 'useSemanticGrounding',
    id: 'cfg-semantic',
    label: 'Use semantically related documentation (search across all docs)',
  },
  {
    key: 'useLinkedDocs',
    id: 'cfg-linked',
    label: 'Use the documentation of linked nodes (the dotted cross-links between branches)',
  },
];

const normalise = (c: Partial<DocConfig>): DocConfig => ({
  designGuidelines: c.designGuidelines || '',
  generalTerms: c.generalTerms || '',
  useExistingDoc: c.useExistingDoc !== false,
  useNeighborDocs: c.useNeighborDocs !== false,
  useSemanticGrounding: c.useSemanticGrounding !== false,
  useLinkedDocs: c.useLinkedDocs !== false,
});

export function AiDocConfig({ host }: { host: HTMLElement }) {
  const s = useAppState();
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState<DocConfig>(EMPTY);
  const [showHelp, setShowHelp] = useState(false);
  const [status, setStatus] = useState('');
  const statusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    host.classList.toggle('show', open);
  }, [open, host]);

  // Fetched on every open rather than cached here: an admin who saved from another tab, or
  // another admin entirely, must not be shown a stale form and then overwrite them with it.
  useEffect(() => {
    if (!open) return;
    let live = true;
    setStatus('');
    void apiRequest<Partial<DocConfig>>('/api/ai/doc-config').then(
      (c) => {
        if (!live) return;
        const next = normalise(c);
        setCfg(next);
        callAction('setAiDocCfgCache', next);
      },
      () => {
        /* keep whatever is shown; the screen is still usable and Save still works */
      },
    );
    return () => {
      live = false;
    };
  }, [open]);

  useEffect(
    () => () => {
      if (statusTimer.current) clearTimeout(statusTimer.current);
    },
    [],
  );

  const api = useRef<AiDocConfigHandle>({ open: () => {}, close: () => {} });
  api.current.open = useCallback(() => setOpen(true), []);
  api.current.close = useCallback(() => setOpen(false), []);
  useEffect(() => {
    registerHandle('aidocConfig', api.current);
  }, []);

  if (!open) return null;

  const save = async () => {
    setStatus('Saving…');
    try {
      await apiRequest('/api/ai/doc-config', { method: 'PUT', body: cfg });
      callAction('setAiDocCfgCache', cfg); // keep the popup's inherit labels honest
      setStatus('Saved ✓');
      if (statusTimer.current) clearTimeout(statusTimer.current);
      statusTimer.current = setTimeout(() => setStatus(''), 1400);
    } catch {
      setStatus('Could not save.');
    }
  };

  const set = <K extends keyof DocConfig>(key: K, value: DocConfig[K]) =>
    setCfg((c) => ({ ...c, [key]: value }));

  return (
    <>
      <div className="perm-head">
        <h2>
          <svg className="ico">
            <use href="#i-ai" />
          </svg>
          AI documentation context
        </h2>
        <PresenceStack />

        {/* The one language picker (V0.53.5) — the same control every header carries. */}
        <LanguagePicker title="Language" />

        <button
          className={showHelp ? 'btn on' : 'btn'}
          id="aidoccfg-info"
          onClick={() => setShowHelp((h) => !h)}
        >
          <svg className="ico">
            <use href="#i-info" />
          </svg>
          <span>Information</span>
        </button>
        <button
          className="btn"
          id="aidoccfg-to-generate"
          onClick={() => {
            setOpen(false);
            callAction('openAiDocModal');
          }}
        >
          <svg className="ico">
            <use href="#i-ai" />
          </svg>
          <span>Generate documentation</span>
        </button>
        <button
          className="btn"
          id="aidoccfg-to-targets"
          onClick={() => {
            setOpen(false);
            callAction('openTargets');
          }}
        >
          <svg className="ico">
            <use href="#i-server" />
          </svg>
          <span>Targets</span>
        </button>
        <button className="btn" id="aidoccfg-close" onClick={() => setOpen(false)}>
          <svg className="ico">
            <use href="#i-x" />
          </svg>
          <span>Back to InfoStation</span>
        </button>
      </div>

      <div className="perm-wrap">
        <div className="perm-inner">
          {/* The guide prose has one home — i18n/help.ts — which the AI-doc info buttons and
              the help guide both read, so they cannot drift apart. */}
          {showHelp ? (
            <div
              id="aidoccfg-help"
              className="cfg-help"
              dangerouslySetInnerHTML={{ __html: helpText('context', s.currentLocale) }}
            />
          ) : null}

          <p id="aidoccfg-intro" style={{ fontSize: 13, color: 'var(--ink-dim)', margin: '0 0 16px' }}>
            These settings are applied to <b>every</b> AI documentation generation, in addition to
            each node&apos;s own AI prompt.
          </p>

          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
            Design &amp; style guidelines
          </label>
          <textarea
            className="fld cfg-textarea"
            id="cfg-design"
            placeholder="How docs should be structured and written — tone, headings, length, audience…"
            value={cfg.designGuidelines}
            onChange={(e) => set('designGuidelines', e.target.value)}
          />

          <label style={{ display: 'block', fontWeight: 600, margin: '18px 0 6px' }}>
            General terms / glossary
          </label>
          <textarea
            className="fld cfg-textarea"
            id="cfg-terms"
            placeholder="Shared terminology and definitions the AI should use consistently…"
            value={cfg.generalTerms}
            onChange={(e) => set('generalTerms', e.target.value)}
          />

          <label style={{ display: 'block', fontWeight: 600, margin: '18px 0 6px' }}>
            Context functions
          </label>
          {GROUNDING.map((g) => (
            <label className="filterrow" key={g.id}>
              <input
                type="checkbox"
                id={g.id}
                checked={cfg[g.key] as boolean}
                onChange={(e) => set(g.key, e.target.checked as DocConfig[typeof g.key])}
              />
              <span>{g.label}</span>
            </label>
          ))}

          <p style={{ fontSize: 12, color: 'var(--ink-faint)', margin: '10px 0 0' }}>
            These are the defaults. Any node can override them for itself in its own AI
            documentation panel.
          </p>
        </div>
      </div>

      <div id="aidoc-foot">
        <button className="pbtn" id="aidoccfg-cancel" onClick={() => setOpen(false)}>
          Cancel
        </button>
        <button className="pbtn primary" id="aidoccfg-save" onClick={save}>
          Save
        </button>
        <span id="aidoccfg-status">{status}</span>
      </div>
    </>
  );
}
